'use strict';

/**
 * Keeps product prices in step with the Ordergroove subscribe option (SC-17575).
 *
 * The server renders the standard price into every `.prices .price` and the subscribed shopper's
 * price into an inert `<template class="og-subscription-price">`. This module swaps one for the
 * other in every price display of a "context" whenever that context's widget `subscribed`
 * attribute changes, and again whenever a variant change brings in a new pair of prices.
 *
 * There are two independent contexts, because a quick view can be open on top of a PDP and each
 * has its own widget: the product page (main and sticky prices) and the quick view modal.
 */

var WIDGET_POLL_MS = 300;
var WIDGET_POLL_MAX = 40;
// a variant request that never answers must not freeze the swap for the rest of the page's life
var PENDING_TIMEOUT_MS = 10000;

var contexts = [];
var observer = null;

/**
 * Returns the markup only if it is a usable price block (an element with the `price` class).
 * Anything else would leave the page without a price element for later swaps to find, so it is
 * refused and the server-rendered price stays.
 * @param {string} html - candidate price markup
 * @returns {string|null} the trimmed markup, or null if it is not a price block
 */
function usable(html) {
    var trimmed = typeof html === 'string' ? html.trim() : '';
    return /class="[^"]*\bprice\b/.test(trimmed) ? trimmed : null;
}

/**
 * Returns the subscription markup only if it carries an actual price. A price block that was
 * rendered without a price object is an empty `.price` shell, and swapping it in would blank
 * the displayed price.
 * @param {string} html - candidate subscription price markup
 * @returns {string|null} the trimmed markup, or null if it holds no price
 */
function usableSubscription(html) {
    var trimmed = usable(html);
    if (trimmed && !/formatted-price="[^"]+"/.test(trimmed)) {
        return null;
    }
    return trimmed;
}

/**
 * Re-renders every context. Runs when any widget's `subscribed` attribute changes.
 * @returns {void}
 */
function renderAll() {
    contexts.forEach(function (context) {
        context.render();
    });
}

/**
 * Watches every widget's `subscribed` attribute. The attribute is what add to cart already
 * reads, so the displayed price and the item that gets added cannot disagree.
 * @returns {void}
 */
function observeWidgets() {
    if (observer || typeof MutationObserver === 'undefined') {
        return;
    }
    observer = new MutationObserver(renderAll);
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['subscribed'] });
}

/**
 * Creates a swap for one context.
 * @param {Object} config - selectors that scope the context
 * @param {string} config.template - the inert subscription price template
 * @param {string} config.prices - every price display in the context
 * @param {string} config.subscribed - the widget element while the subscribe option is selected
 * @param {string} config.widget - the widget element, subscribed or not
 * @returns {Object} the swap
 */
function createSwap(config) {
    var standardHtml = null;
    var subscriptionHtml = null;
    var active = false;
    // true from the moment a variant change starts until its prices arrive: the cached HTML
    // belongs to the previous variant and must not be applied to the next one
    var pending = false;
    var pendingTimer = null;
    var pollTimer = null;

    var swap = {
        /**
         * Shows the subscription price when the widget says subscribed, the standard price
         * otherwise. Does nothing unless the context has a subscription price to show.
         * @returns {void}
         */
        render: function () {
            if (!active || pending || !subscriptionHtml || !standardHtml) {
                return;
            }
            $(config.prices).replaceWith($(config.subscribed).length > 0 ? subscriptionHtml : standardHtml);
        },

        /**
         * Called when a variant change has replaced the standard price. Takes the new variant's
         * prices from the response, then re-applies the shopper's current choice.
         * @param {Object} product - `response.product` from the variation call
         * @returns {void}
         */
        onVariantResponse: function (product) {
            if (!active) {
                return;
            }
            pending = false;
            clearTimeout(pendingTimer);
            subscriptionHtml = usableSubscription(product && product.subscriptionPriceHtml);
            if (product && product.price && product.price.html) {
                standardHtml = usable(product.price.html);
            }
            if (subscriptionHtml) {
                observeWidgets();
            }
            swap.render();
        },

        /**
         * Called when a variant change starts. Holds the swap until the new prices arrive.
         * @returns {void}
         */
        markPending: function () {
            if (active) {
                pending = true;
                // if the request fails the old variant is still on screen and the cached prices
                // still belong to it, so releasing the hold without new data is correct
                clearTimeout(pendingTimer);
                pendingTimer = setTimeout(function () {
                    pending = false;
                }, PENDING_TIMEOUT_MS);
            }
        },

        /**
         * Called after another script has rewritten the standard price in place (the separate
         * pricing cache fills it in after page load). Takes the fresh standard price as the
         * one to restore, then re-applies the shopper's current choice.
         * @returns {void}
         */
        refreshStandard: function () {
            if (!active || !subscriptionHtml) {
                return;
            }
            var $standard = $(config.prices).first();
            if ($standard.length) {
                standardHtml = usable($standard.prop('outerHTML'));
            }
            swap.render();
        },

        /**
         * Starts the swap if the context has a subscription price. The widget mounts
         * asynchronously, so wait for it before the first render: the subscribe option is
         * selected by default, so the subscription price should replace the server-rendered
         * standard price as soon as the widget appears.
         * @returns {void}
         */
        init: function () {
            var $template = $(config.template).first();
            var $standard = $(config.prices).first();
            subscriptionHtml = $template.length ? usableSubscription($template.html()) : null;
            standardHtml = $standard.length ? usable($standard.prop('outerHTML')) : null;
            active = Boolean(subscriptionHtml && standardHtml);
            // never leave an earlier poll running (a quick view can be reopened quickly)
            clearInterval(pollTimer);
            if (!active) {
                return;
            }
            if (contexts.indexOf(swap) === -1) {
                contexts.push(swap);
            }
            observeWidgets();

            var attempts = 0;
            pollTimer = setInterval(function () {
                attempts += 1;
                if ($(config.widget).length || attempts >= WIDGET_POLL_MAX) {
                    clearInterval(pollTimer);
                    swap.render();
                }
            }, WIDGET_POLL_MS);
        },

        /**
         * Stops the swap, e.g. when its container has been removed.
         * @returns {void}
         */
        stop: function () {
            active = false;
            pending = false;
            clearInterval(pollTimer);
            clearTimeout(pendingTimer);
            subscriptionHtml = null;
            standardHtml = null;
        }
    };
    return swap;
}

var page = createSwap({
    template: '.og-wrapper template.og-subscription-price',
    prices: '.product-detail:not(.product-quickview) .prices .price, .sticky-fixed .prices .price',
    subscribed: '.og-wrapper og-offer[subscribed], .og-wrapper .og-offer[subscribed]',
    widget: '.og-wrapper og-offer, .og-wrapper .og-offer'
});

var quickView = createSwap({
    template: '.product-quickview template.og-subscription-price',
    prices: '.product-quickview .prices .price',
    subscribed: '.product-quickview og-offer[subscribed], .product-quickview .og-offer[subscribed]',
    widget: '.product-quickview og-offer, .product-quickview .og-offer'
});

/**
 * Picks the swap that owns a product container.
 * @param {jQuery} [$productContainer] - container the variant response belongs to
 * @returns {Object} quick view swap inside a quick view, page swap otherwise
 */
function forContainer($productContainer) {
    var inQuickView = $productContainer && ($productContainer.hasClass('product-quickview')
        || $productContainer.closest('.product-quickview').length > 0);
    return inQuickView ? quickView : page;
}

/**
 * A variant change has started in a container: stop swapping until its prices arrive.
 * @param {jQuery} [$productContainer] - container the change belongs to
 * @returns {void}
 */
function beforeVariant($productContainer) {
    forContainer($productContainer).markPending();
}

/**
 * A variant change has finished in a container: take its prices and apply the shopper's choice.
 * @param {Object} product - `response.product` from the variation call
 * @param {jQuery} [$productContainer] - container the change belongs to
 * @returns {void}
 */
function afterVariant(product, $productContainer) {
    forContainer($productContainer).onVariantResponse(product);
}

module.exports = {
    page: page,
    quickView: quickView,
    forContainer: forContainer,
    beforeVariant: beforeVariant,
    afterVariant: afterVariant,
    init: page.init,
    onVariantResponse: function (product, $productContainer) {
        forContainer($productContainer).onVariantResponse(product);
    },
    refreshStandard: page.refreshStandard,
    render: page.render
};
